import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { create } from 'zustand';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';

export interface ToastInput {
  title: string;
  description?: string;
  variant?: 'default' | 'success' | 'error';
  /** e.g. `{ label: 'Undo', onClick }` for destructive actions. */
  action?: { label: string; onClick: () => void };
  durationMs?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

const useToasts = create<{ items: ToastItem[] }>(() => ({ items: [] }));
let nextId = 1;

export function dismissToast(id: number): void {
  useToasts.setState((s) => ({ items: s.items.filter((t) => t.id !== id) }));
}

export function toast(input: ToastInput | string): number {
  const item: ToastItem = { id: nextId++, ...(typeof input === 'string' ? { title: input } : input) };
  useToasts.setState((s) => ({ items: [...s.items.slice(-3), item] }));
  // Toasts with an action stay longer so there is time to hit Undo.
  setTimeout(() => dismissToast(item.id), item.durationMs ?? (item.action ? 8000 : 4000));
  return item.id;
}

toast.success = (title: string, description?: string) => toast({ title, description, variant: 'success' });
toast.error = (err: unknown, title?: string) =>
  toast(
    title
      ? { title, description: errorMessage(err), variant: 'error' }
      : { title: typeof err === 'string' ? err : errorMessage(err), variant: 'error' },
  );
/** Confirmation toast with Undo for a destructive action that has already been applied. */
toast.undo = (title: string, onUndo: () => void) =>
  toast({ title, action: { label: 'Undo', onClick: onUndo } });

const ICONS = {
  default: <Info className="size-4 text-fg-muted" />,
  success: <CheckCircle2 className="size-4 text-success" />,
  error: <AlertCircle className="size-4 text-danger" />,
};

export function Toaster() {
  const items = useToasts((s) => s.items);
  return (
    <div
      role="region"
      aria-label="Notifications"
      className="pointer-events-none fixed right-4 bottom-4 z-[70] flex w-88 max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {items.map((t) => (
        <div
          key={t.id}
          role={t.variant === 'error' ? 'alert' : 'status'}
          className={cn(
            'pointer-events-auto flex animate-slide-in items-start gap-2.5 rounded-lg border border-border bg-surface p-3 shadow-pop',
          )}
        >
          <span className="mt-0.5">{ICONS[t.variant ?? 'default']}</span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-fg">{t.title}</p>
            {t.description ? <p className="mt-0.5 text-sm text-fg-muted">{t.description}</p> : null}
          </div>
          {t.action ? (
            <button
              className="shrink-0 rounded px-1.5 py-0.5 text-sm font-semibold text-accent-text hover:bg-accent-soft"
              onClick={() => {
                t.action!.onClick();
                dismissToast(t.id);
              }}
            >
              {t.action.label}
            </button>
          ) : null}
          <button
            aria-label="Dismiss"
            className="shrink-0 rounded p-0.5 text-fg-subtle hover:bg-hover hover:text-fg"
            onClick={() => dismissToast(t.id)}
          >
            <X className="size-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
